import { z } from "zod";
import { AgencyIdSchema, ProjectIdSchema, ReportIdSchema, RevisionIdSchema, RunIdSchema, } from "../primitives/ids.js";
import { ReportTypeSchema } from "./report-lifecycle.js";
import { PackPinSchema } from "./generation-run.js";
import { TraceContextSchema } from "./trace.js";
/** generation-job-payload.v1.0 — BullMQ / worker job data (Tier 0 Step 3 + Step 4). */
export const GenerationJobKindSchema = z.enum([
    "GENERATE_AND_QA",
    "EXPORT_SITE_AND_MINIMAL_DK",
    "EXPORT_SITE_AND_DK_V1",
    "GENERATE_REPORT",
]);
export const GenerationJobGeneratePayloadSchema = z
    .object({
    schemaVersion: z.literal("generation-job-payload.v1.0"),
    jobKind: z.literal("GENERATE_AND_QA"),
    runId: RunIdSchema,
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    businessGraphRevisionId: RevisionIdSchema,
    packPin: PackPinSchema,
    trace: TraceContextSchema,
    snapshotRef: z.string().min(1).optional(),
})
    .strict();
export const GenerationJobExportPayloadSchema = z
    .object({
    schemaVersion: z.literal("generation-job-payload.v1.0"),
    jobKind: z.literal("EXPORT_SITE_AND_MINIMAL_DK"),
    runId: RunIdSchema,
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    businessGraphRevisionId: RevisionIdSchema,
    /** Must match persisted GenerationRun.artifactSetHash at enqueue time (stale-QA guard). */
    artifactSetHash: z.string().min(1),
    trace: TraceContextSchema,
})
    .strict();
export const GenerationJobExportDkV1PayloadSchema = z
    .object({
    schemaVersion: z.literal("generation-job-payload.v1.0"),
    jobKind: z.literal("EXPORT_SITE_AND_DK_V1"),
    runId: RunIdSchema,
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    businessGraphRevisionId: RevisionIdSchema,
    artifactSetHash: z.string().min(1),
    trace: TraceContextSchema,
})
    .strict();
export const GenerationJobReportPayloadSchema = z
    .object({
    schemaVersion: z.literal("generation-job-payload.v1.0"),
    jobKind: z.literal("GENERATE_REPORT"),
    reportId: ReportIdSchema,
    agencyId: AgencyIdSchema,
    projectId: ProjectIdSchema,
    runId: RunIdSchema,
    businessGraphRevisionId: RevisionIdSchema,
    artifactSetHash: z.string().min(1),
    reportType: ReportTypeSchema,
    trace: TraceContextSchema,
})
    .strict();
export const GenerationJobPayloadV1Schema = z.discriminatedUnion("jobKind", [
    GenerationJobGeneratePayloadSchema,
    GenerationJobExportPayloadSchema,
    GenerationJobExportDkV1PayloadSchema,
    GenerationJobReportPayloadSchema,
]);
export function assertGenerateJobPayload(raw) {
    const p = GenerationJobPayloadV1Schema.parse(raw);
    if (p.jobKind !== "GENERATE_AND_QA") {
        throw new Error(`expected GENERATE_AND_QA job payload, got ${p.jobKind}`);
    }
}
/** BullMQ queue name for generation/export/report jobs (API producer + worker consumer). */
export const GENERATION_QUEUE_NAME = "tier0-generation";
/** Deterministic BullMQ job id — ties enqueue dedupe to tenant scope + run. */
export function generationEnqueueJobId(agencyId, projectId, runId) {
    return `enr:${agencyId}:${projectId}:${runId}`;
}
export function exportEnqueueJobId(agencyId, projectId, runId) {
    return `enr:export:${agencyId}:${projectId}:${runId}`;
}
export function reportEnqueueJobId(agencyId, projectId, reportId) {
    return `enr:report:${agencyId}:${projectId}:${reportId}`;
}
/** Build GENERATE_AND_QA BullMQ payload from persisted run state (API + worker recovery). */
export function generationJobPayloadFromPersistedRun(run) {
    const trace = TraceContextSchema.parse(run.traceJson);
    const packPin = PackPinSchema.parse(run.packPinJson);
    return GenerationJobGeneratePayloadSchema.parse({
        schemaVersion: "generation-job-payload.v1.0",
        jobKind: "GENERATE_AND_QA",
        runId: run.runId,
        agencyId: run.agencyId,
        projectId: run.projectId,
        businessGraphRevisionId: run.businessGraphRevisionId,
        packPin,
        trace,
        snapshotRef: run.runId,
    });
}
//# sourceMappingURL=generation-job-payload.js.map